import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

const COLORS = ["#FACC15", "#F43F5E", "#FB923C", "#FFFFFF", "#EC4899", "#FDE68A", "#A855F7", "#34D399"];
const BURSTS = Array.from({ length: 9 }, (_, b) => ({ x: 12 + ((b * 41) % 76), y: 14 + ((b * 29) % 46), d: (b % 9) * 0.55 }));
const MEDAL = ["🥇", "🥈", "🥉"];

const Burst = ({ x, y, d }) => (
  <span className="absolute" style={{ left: `${x}%`, top: `${y}%` }}>
    {Array.from({ length: 22 }, (_, i) => {
      const a = (i / 22) * Math.PI * 2, r = 90 + (i % 4) * 22;
      return <span key={i} className="fw-spark" style={{ background: COLORS[(i + d * 4) % COLORS.length | 0], "--dx": `${Math.cos(a) * r}px`, "--dy": `${Math.sin(a) * r}px`, animationDelay: `${d}s` }} />;
    })}
  </span>
);

const Hero = ({ h, i }) => (
  <li className="flex flex-col items-center gap-1 min-w-0 fw-rise" style={{ animationDelay: `${0.6 + i * 0.2}s` }} data-testid={`quest-finale-hero-${i}`}>
    <span className="relative"><UserAvatar userId={h.user_id} name={h.name} picture={h.picture} size={i === 0 ? "w-20 h-20" : "w-14 h-14"} noStory /><span className="absolute -bottom-1 -right-1 text-xl">{MEDAL[i]}</span></span>
    <span className="text-sm font-extrabold truncate max-w-[6.5rem]">{h.name.split(" ")[0]}</span>
    <span className="text-[11px] font-bold text-amber-200">{h.total} Beiträge</span>
  </li>
);

export function QuestFinale({ end, onClose }) {
  const [q, setQ] = useState(null);
  useEffect(() => {
    api.get("/quest").then(({ data }) => setQ(data)).catch(() => {});
    const t = setTimeout(onClose, 12000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const top = (q?.top || []).slice(0, 3);
  const podium = top.length === 3 ? [top[1], top[0], top[2]] : top;
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-[#1a0610]/80 backdrop-blur-sm wheel-fade" data-testid="quest-finale" role="dialog" aria-label="Community-Ziel erreicht">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>{BURSTS.map((b, i) => <Burst key={i} {...b} />)}</div>
      <div className="relative w-full max-w-sm text-center text-white fw-rise">
        <button type="button" onClick={onClose} aria-label="Schließen" data-testid="quest-finale-close" className="absolute -top-2 right-0 z-10 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><X className="w-4 h-4" /></button>
        <p className="font-display font-black text-7xl leading-none bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent drop-shadow" data-testid="quest-finale-pct">100 %</p>
        <h2 className="mt-3 font-display text-2xl font-extrabold">Ihr habt es geschafft! 🎉</h2>
        <p className="mt-1 text-sm text-white/80">Alle Wochenend-Ziele erreicht – jeder Mitmacher bekommt {q?.stake ?? 500} Punkte, sobald die Aufgabe endet.</p>
        {top.length > 0 && (
          <div className="mt-6 rounded-3xl bg-white/10 ring-1 ring-white/20 p-4" data-testid="quest-finale-heroes">
            <p className="flex items-center justify-center gap-1.5 text-xs font-extrabold uppercase tracking-widest text-amber-200"><Trophy className="w-4 h-4" /> Eure Aufgaben-Helden</p>
            <ol className="mt-4 flex items-end justify-center gap-4">{podium.map((h) => <Hero key={h.user_id} h={h} i={top.indexOf(h)} />)}</ol>
          </div>
        )}
        <Link to="/community-aufgabe" onClick={onClose} className="mt-6 inline-flex items-center justify-center w-full h-12 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#4a2a08] font-black shadow-lg" data-testid="quest-finale-cta">Zur Community-Aufgabe</Link>
      </div>
    </div>
  );
}
